import { useCallback, useEffect, useState } from "react";
import SiteVisitManagement from "./components/SiteVisitManagement";
import { AssignmentData, Venue } from "./types/site-visit";
import apiService from "../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../components/common/ShowToast";
import formatDate from "../../utils/FormatDate";

export interface ApiResponse<T = unknown> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

interface SiteVisitApiItem {
  id: number;
  status: string;
  scheduled_date_time?: string | null;
  notes?: string | null;
  venue?: {
    id?: number;
    name?: string;
    venue_type?: string;
    address_one?: string;
    address_two?: string;
    city?: string;
    suburb?: string;
    country?: string;
  };
  assignees?: Array<{
    first_name?: string;
    last_name?: string;
  }>;
}

function SiteVisitList() {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("all");

  const [currentStatus, setCurrentStatus] = useState("pending");

  const fetchVenues = useCallback(async (status: string) => {
    setIsLoading(true);
    try {
      const params: Record<string, string | number> = {
        page: currentPage + 1,
        page_size: pageSize,
        status,
      };

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      if (category !== "all") {
        params.venue_type = category;
      }

      const response = await apiService.get<ApiResponse<SiteVisitApiItem>>(
        "/api/venues/site-visit/",
        params
      );

      const results = response.data.results;
      if (!Array.isArray(results)) throw new Error("Invalid API response");

      const allowedStatuses: Venue["status"][] = [
        "pending",
        "assigned",
        "awaiting_review",
        "approved",
        "rejected",
      ];

      const mappedVenues: Venue[] = results.map((item) => ({
        id: item.id,
        venueId: item.venue?.id ?? item.id,
        name: item.venue?.name ?? "Unknown Venue",
        type: item.venue?.venue_type ?? "",
        address: [
          item.venue?.address_one,
          item.venue?.address_two,
          item.venue?.city,
          item.venue?.suburb,
          item.venue?.country,
        ]
          .filter(Boolean)
          .join(", "),
        status: allowedStatuses.includes(item.status as Venue["status"])
          ? (item.status as Venue["status"])
          : "pending",
        assignee: item.assignees?.[0]
          ? `${item.assignees[0].first_name} ${item.assignees[0].last_name}`
          : "",
        dueDate: formatDate(item.scheduled_date_time),
        notes: item.notes ?? "",
      }));

      setVenues(mappedVenues);
      setTotalItems(response.data.count);
    } catch (err: unknown) {
      // handle errors...
      console.log("Error :", err);
    } finally {
      setIsLoading(false);
    }
  }, [category, currentPage, pageSize, searchTerm]);

  useEffect(() => {
    fetchVenues(currentStatus);
  }, [currentStatus, fetchVenues]);

  const handleAssignVenue = async (
    siteVisitId: number, // use number type
    data: AssignmentData
  ) => {
    try {
      const assigneeId = parseInt(data.assigneeId, 10);
      if (isNaN(assigneeId)) throw new Error("Invalid assignee ID.");

      const dateObj = new Date(data.dueDate);
      if (isNaN(dateObj.getTime())) throw new Error("Invalid due date.");

      const payload = {
        scheduled_date_time: dateObj.toISOString(),
        assignee_ids: [assigneeId],
        notes: data.notes?.trim() || null,
        status: "assigned",
      };

      // PATCH to specific site visit using its ID
      await apiService.patch(
        `/api/venues/site-visit/${siteVisitId}/`,
        payload
      );

      showSuccessToast("Site visit updated successfully");
      fetchVenues(currentStatus);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ||
        (err as { message?: string })?.message ||
        "Failed to update site visit.";
      console.error("Assignment error:", message);
      showErrorToast(message);
    }
  };

  const handleSendEmail = async (
    _userId: string,
    _venueId: string
  ): Promise<void> => {
    void _userId;
    void _venueId;
    await new Promise((resolve) => setTimeout(resolve, 500));
  };

  return (
    <SiteVisitManagement
      venues={venues}
      onAssignVenue={handleAssignVenue}
      onSendEmail={handleSendEmail}
      isLoading={isLoading}
      page={currentPage}
      pageSize={pageSize}
      totalItems={totalItems}
      onPageChange={setCurrentPage}
      onPageSizeChange={setPageSize}
      currentStatus={currentStatus}
      onStatusChange={setCurrentStatus}
      searchTerm={searchTerm}
      onSearchTermChange={setSearchTerm}
      category={category}
      onCategoryChange={setCategory}
    />
  );
}

export default SiteVisitList;
